/**
 * Prosumer Calculator - Calculation Hook
 *
 * Manages API calls to calculateProsumerTariffs mutation.
 * Follows the pattern from useG14QuickCalc.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { getAmplifyClient } from '../../../lib/amplifyClient';
import type {
  ProsumerFormValues,
  ProsumerCalculationResult,
} from '../types';
import { TariffType } from '../types';

// =============================================================================
// TRANSFORM
// =============================================================================

interface BackendInput {
  distributor: string;
  year: number;
  region?: string;
  deviceProfile: {
    hasPV: boolean;
    hasEV: boolean;
    hasHeatPump: boolean;
  };
  pvConfig: unknown;
  evConfig: unknown;
  heatPumpConfig: unknown;
  annualConsumptionKwh: number;
  monthlyConsumptionKwh: number[];
  hourlyConsumptionData?: unknown[];
  hourlyExportData?: unknown[];
  tariffTypes: string[];
}

export function transformToBackendInput(values: ProsumerFormValues): BackendInput {
  const tariffTypes = values.tariffTypes.length > 0
    ? values.tariffTypes
    : [TariffType.G11, TariffType.G12, TariffType.G12w];

  const input: BackendInput = {
    distributor: values.distributor,
    year: values.year,
    ...(values.region !== undefined && { region: values.region }),
    deviceProfile: {
      hasPV: values.hasPV,
      hasEV: values.hasEV,
      hasHeatPump: values.hasHeatPump,
    },
    pvConfig: values.pvConfig,
    evConfig: values.evConfig,
    heatPumpConfig: values.heatPumpConfig,
    annualConsumptionKwh: values.annualConsumptionKwh,
    monthlyConsumptionKwh: values.monthlyConsumptionKwh,
    tariffTypes: tariffTypes.map(String),
  };

  // Map CSV hourly data when available
  if (values.csvData) {
    if (values.csvData.hourlyConsumptionData.length > 0) {
      input.hourlyConsumptionData = values.csvData.hourlyConsumptionData;
    }
    if (values.csvData.hourlyExportData.length > 0) {
      input.hourlyExportData = values.csvData.hourlyExportData;
    }
  }

  return input;
}

// =============================================================================
// RETURN TYPE
// =============================================================================

export interface UseProsumerCalculationReturn {
  result: ProsumerCalculationResult | null;
  loading: boolean;
  error: string | null;
  calculate: (formValues: ProsumerFormValues) => Promise<void>;
  reset: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

export function useProsumerCalculation(): UseProsumerCalculationReturn {
  const [result, setResult] = useState<ProsumerCalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const calculate = useCallback(async (formValues: ProsumerFormValues) => {
    setLoading(true);
    setError(null);
    setResult(null);
    cancelledRef.current = false;

    try {
      const client = getAmplifyClient();
      const backendInput = transformToBackendInput(formValues);

      const response = await client.mutations.calculateProsumerTariffs({
        input: JSON.stringify(backendInput),
      } as any);

      if (cancelledRef.current) return;

      if (response.errors && response.errors.length > 0) {
        const msg = response.errors[0]?.message;
        throw new Error(msg || 'Błąd serwera podczas obliczeń');
      }

      if (!response.data) {
        throw new Error('Brak danych z serwera');
      }

      // AppSync double-encodes Lambda string responses: parse until we get an object
      let parsed: unknown = typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data;
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }

      setResult(parsed as ProsumerCalculationResult);
    } catch (err) {
      if (cancelledRef.current) return;
      console.error('[useProsumerCalculation]', err);
      setError(err instanceof Error ? err.message : 'Nie udało się wykonać obliczeń');
      setResult(null);
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setLoading(false);
  }, []);

  return { result, loading, error, calculate, reset };
}
